import { useState, useEffect } from 'react'
import { Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { useTheme } from '../../context/ThemeContext'
import {
  Layers,
  Users,
  User,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  FolderKanban,
} from 'lucide-react'
import MentorHome from './MentorHome'
import MentorProjects from './MentorProjects'
import MentorParticipants from './MentorParticipants'
import MentorProfile from './MentorProfile'
import MentorReviewWorkspace from './MentorReviewWorkspace'

/**
 * MentorPortal — dedicated shell for Project Lab industry mentors.
 * Editorial, cardless, typography-led. Not the academic LMS.
 */
export default function MentorPortal({ context }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useAuthStore()
  const { darkMode, toggleDarkMode } = useTheme()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const mentor = context.mentor

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  const navLinks = [
    { to: '/mentor/dashboard', icon: Layers, label: 'Overview' },
    { to: '/mentor/projects', icon: FolderKanban, label: 'My Projects' },
    { to: '/mentor/participants', icon: Users, label: 'Participants' },
    { to: '/mentor/profile', icon: User, label: 'Profile' },
  ]

  const isActive = (to) => location.pathname.startsWith(to)

  return (
    <div className={`cinema-page min-h-screen ${darkMode ? '' : 'admin-light'}`}>
      <div className="cinema-grain" aria-hidden="true" />

      {/* ── Top Nav ──────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-30 cinema-glass-strong">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 lg:hidden text-cinema-muted hover:text-white transition-colors"
                aria-label="Toggle navigation"
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <img
                src={darkMode ? '/Logos/SMA LOGO  DARK MODE.png' : '/Logos/SMA LOGO  VERSION 2-01.png'}
                alt="SMA Project Lab"
                className="h-8"
              />
              <div className="hidden md:flex flex-col">
                <span className="font-display text-white font-medium text-sm leading-none">Project Lab</span>
                <span className="cinema-label mt-1 text-xs">Mentor</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/mentor/profile"
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 cinema-glass border border-white/8 hover:border-cinema-gold/30 text-cinema-soft hover:text-white transition-all"
                title="My profile"
              >
                <User size={14} className="text-cinema-gold" />
                <span className="font-sans text-xs">{mentor.professional_name}</span>
              </Link>
              <button
                onClick={toggleDarkMode}
                className="flex items-center justify-center w-9 h-9 cinema-glass border border-white/8 hover:border-cinema-gold/30 text-cinema-muted hover:text-cinema-gold transition-all"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label="Toggle theme"
              >
                {darkMode ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-1.5 cinema-glass border border-white/8 hover:border-red-500/40 text-cinema-muted hover:text-red-400 text-xs font-sans transition-all"
              >
                <LogOut size={14} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/70 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex">
        {/* ── Sidebar ──────────────────────────────────────────────── */}
        <aside className={`
          fixed top-16 left-0 bottom-0 w-56 z-40 overflow-y-auto transition-transform duration-300 ease-in-out
          cinema-glass-strong border-r border-white/5 p-4
          lg:static lg:translate-x-0 lg:flex-shrink-0 lg:h-[calc(100vh-4rem)] lg:sticky lg:top-16
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="mb-6 pb-4 border-b border-white/8">
            <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-gold font-medium">
              Industry Mentor
            </span>
            <p className="text-xs text-cinema-soft mt-1 leading-relaxed">
              {mentor.professional_name}
            </p>
            {mentor.professional_title && (
              <p className="text-[11px] text-cinema-muted mt-0.5">{mentor.professional_title}</p>
            )}
          </div>

          <div className="space-y-1">
            {navLinks.map(({ to, icon: Icon, label }) => (
              <Link
                key={to}
                to={to}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-sans transition-all duration-200 ${
                  isActive(to)
                    ? 'bg-cinema-glow/10 text-cinema-glow border-l-2 border-cinema-glow'
                    : 'text-cinema-muted hover:text-white hover:bg-white/5 border-l-2 border-transparent'
                }`}
              >
                <Icon size={16} />
                {label}
              </Link>
            ))}
          </div>
        </aside>

        {/* ── Main Content ──────────────────────────────────────────── */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 relative">
          <Routes>
            <Route path="dashboard" element={<MentorHome mentor={mentor} />} />
            <Route path="projects" element={<MentorProjects />} />
            <Route path="projects/:projectId" element={<MentorProjects />} />
            <Route path="participants" element={<MentorParticipants />} />
            <Route path="enrollments/:enrollmentId" element={<MentorReviewWorkspace />} />
            <Route path="profile" element={<MentorProfile />} />
            <Route path="*" element={<Navigate to="/mentor/dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}
